import { createContext, FC, useContext, useEffect, useMemo, useState } from 'react';
import { matchPath, useLocation } from 'react-router-dom';

export interface RouteContextValue {
  customTitle: string;
  routeTitle: string;
  setCustomTitle: (title: string) => void;
  setTitlePrefix: (prefix: string) => void;
}

export const RouteContext = createContext<RouteContextValue>({} as RouteContextValue);

export const useRoute = () => {
  const contextValue = useContext(RouteContext);

  useEffect(() => {
    return () => {
      // console.log('useRoute. unmounting');
      contextValue.setCustomTitle('');
    };
  }, []);

  return contextValue;
};

export interface RouteProviderProps {
  routeTitles: Record<string, string>;
}

export const RouteProvider: FC<RouteProviderProps> = ({ children, routeTitles }) => {
  const location = useLocation();

  const [customTitle, setCustomTitle] = useState('');
  const [titlePrefix, setTitlePrefix] = useState('');

  const routeTitle = useMemo(() => {
    // console.log('RouteProvider. location: ', location);
    let result = '';

    Object.keys(routeTitles)
      .sort((a, b) => b.length - a.length)
      .some((key) => {
        if (matchPath(location.pathname, key)) {
          // console.log('RouteProvider. match: ', key);
          result = routeTitles[key] || '';
          return true;
        }
        // console.log('RouteProvider. no match: ', key);
        return false;
      });

    return result;
  }, [location.pathname, routeTitles]);

  useEffect(() => {
    const currentTitle = customTitle || routeTitle;
    const prefix = titlePrefix ? `(${titlePrefix}) ` : '';
    // console.log('RouteProvider. currentTitle: ', currentTitle);

    if (currentTitle) {
      window.document.title = prefix + currentTitle + ' | Kato';
    } else {
      window.document.title = prefix + 'Kato';
    }
    // console.log('RouteProvider. set title: ', window.document.title);
  }, [customTitle, routeTitle, titlePrefix]);

  const contextValue = useMemo(() => {
    const result: RouteContextValue = {
      customTitle,
      routeTitle,
      setCustomTitle,
      setTitlePrefix,
    };

    return result;
  }, [customTitle, routeTitle, setCustomTitle, setTitlePrefix]);

  return <RouteContext.Provider value={contextValue}>{children}</RouteContext.Provider>;
};
